
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Avatar,
  Stack,
  Divider,
} from "@mui/material";

import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";

import Navbar from "../components/Homepage/Navbar/Navbar";
import Footer from "../components/Homepage/Footer/Footer";

const API_URL ="https://rukap.edu.in/leave-api";
 

const leaders = [
  {
    title: "Hon'ble Vice-Chancellor",
    name: "Prof. V. Venkata Basava Rao",
    image: "/person/vicechancellor1.JPG",
    initials: "VC",
    message:
      "Promoting academic excellence, effective governance and digital transformation.",
  },
  {
    title: "University Registrar",
    name: "Prof. B. Vijaya Kumar Naidu",
    image: "/person/registrar-Vijay.JPG",
    initials: "BV",
    message:
      "Supporting efficient administration and digital services for university employees.",
  },
];

export default function LeaveHome() {
  const navigate = useNavigate();

  const [employeeStats, setEmployeeStats] = useState({
    totalEmployees: 0,
    teachingCount: 0,
    nonTeachingCount: 0,
  });

  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);

  useEffect(() => {
    let active = true;

    axios
      .get(`${API_URL}/api/public/employee-stats`)
      .then((response) => {
        if (!active) return;

        const data = response.data;

        if (!data.success) {
          throw new Error("Unable to fetch employee statistics");
        }

        setEmployeeStats({
          totalEmployees: Number(data.totalEmployees) || 0,
          teachingCount: Number(data.teachingCount) || 0,
          nonTeachingCount: Number(data.nonTeachingCount) || 0,
        });

        setStatsError(false);
      })
      .catch((error) => {
        if (!active) return;

        console.error("Employee statistics error:", error);
        setStatsError(true);
      })
      .finally(() => {
        if (active) setStatsLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const statistics = [
    {
      label: "Total Employees",
      value: employeeStats.totalEmployees,
      icon: <GroupsOutlinedIcon />,
    },
    {
      label: "Teaching Staff",
      value: employeeStats.teachingCount,
      icon: <SchoolOutlinedIcon />,
    },
    {
      label: "Non-Teaching Staff",
      value: employeeStats.nonTeachingCount,
      icon: <BusinessCenterOutlinedIcon />,
    },
  ];

  return (
    <>
      <Navbar />

      <Box
        sx={{
          minHeight: "65vh",
          bgcolor: "#f4f6f9",
          py: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Container maxWidth="md">
          {/* Leadership profiles */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: 2,
              mb: 3,
            }}
          >
            {leaders.map((leader) => (
              <Paper
                key={leader.title}
                elevation={0}
                sx={{
                  p: 2,
                  border: "1px solid #dce3ea",
                  borderRadius: 2,
                  bgcolor: "#fff",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                >
                  <Avatar
                    src={leader.image}
                    alt={leader.name}
                    sx={{
                      width: { xs: 60, sm: 68 },
                      height: { xs: 60, sm: 68 },
                      bgcolor: "#0B3C5D",
                      border: "1px solid #e0e6ed",
                      flexShrink: 0,
                    }}
                  >
                    {leader.initials}
                  </Avatar>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: "#0B3C5D",
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        mb: 0.3,
                      }}
                    >
                      {leader.title}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        lineHeight: 1.4,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {leader.name}
                    </Typography>
                  </Box>
                </Stack>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1.5, lineHeight: 1.6 }}
                >
                  {leader.message}
                </Typography>
              </Paper>
            ))}
          </Box>

          {/* Main leave portal */}
          <Paper
            elevation={0}
            sx={{
              maxWidth: 760,
              mx: "auto",
              p: { xs: 2.5, sm: 4 },
              textAlign: "center",
              bgcolor: "#fff",
              border: "1px solid #dce3ea",
              borderTop: "4px solid #0B3C5D",
              borderRadius: 2,
            }}
          >
            <AccountBalanceOutlinedIcon
              sx={{ fontSize: 34, color: "#0B3C5D", mb: 1 }}
            />

            <Typography
              component="h1"
              sx={{
                color: "#0B3C5D",
                fontSize: { xs: "1.1rem", sm: "1.4rem" },
                fontWeight: 400,
                lineHeight: 1.35,
              }}
            >
              Employee Leave Management Portal
            </Typography>

            

            <Divider
              sx={{ my: 1.5, maxWidth: 100, mx: "auto" }}
            />

            <Typography
              color="text.secondary"
              sx={{
                fontSize: "0.9rem",
                mb: 2.5,
                lineHeight: 1.7,
              }}
            >
              Access leave applications, employee details and leave
              balances through the university's online portal.
            </Typography>

            <Button
              variant="contained"
              size="large"
              fullWidth
              startIcon={<LoginRoundedIcon />}
              onClick={() => navigate("/leave-portal/login")}
              sx={{
                maxWidth: 280,
                py: 1.25,
                px: 3,
                bgcolor: "#0B3C5D",
                borderRadius: 1.5,
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#082b43",
                  boxShadow: "none",
                },
              }}
            >
              Login to Leave Portal
            </Button>

            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
              sx={{ mt: 2 }}
            >
              For university employees and authorised administrators
            </Typography>
          </Paper>

          {/* Employee statistics */}
          <Box sx={{ mt: 3, mb: 1 }}>
            <Typography
              component="h2"
              sx={{
                color: "#0B3C5D",
                fontWeight: 700,
                fontSize: "1.1rem",
                textAlign: "center",
                mb: 2,
              }}
            >
              University Employee Statistics
            </Typography>

            {statsError && (
              <Typography
                color="text.secondary"
                variant="body2"
                textAlign="center"
                sx={{ mb: 1.5 }}
              >
                Employee statistics are temporarily unavailable.
              </Typography>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(3, minmax(0, 1fr))",
                },
                gap: 1.5,
              }}
            >
              {statistics.map((item) => (
                <Paper
                  key={item.label}
                  elevation={0}
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    border: "1px solid #dce3ea",
                    borderRadius: 2,
                    bgcolor: "#fff",
                  }}
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      flexShrink: 0,
                      borderRadius: 2,
                      bgcolor: "#eaf1f6",
                      color: "#0B3C5D",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {item.icon}
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: "#0B3C5D",
                        fontSize: "1.45rem",
                        fontWeight: 800,
                        lineHeight: 1.3,
                      }}
                    >
                      {statsLoading
                        ? "—"
                        : item.value.toLocaleString("en-IN")}
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.8rem" }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </>
  );
}
